import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DataObjectIcon from "@mui/icons-material/DataObject";
import TableViewIcon from "@mui/icons-material/TableView";
import PublicIcon from "@mui/icons-material/Public";
import ForumIcon from "@mui/icons-material/Forum";
// Telegram UI hidden — kept for future re-enable.
// import TelegramIcon from "@mui/icons-material/Telegram";
import { Reddit as RedditIcon } from "@mui/icons-material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSearchParams } from "react-router-dom";
import _ from "lodash";

import { useModules } from "../../ModuleContext";
import getEnv from "../../config";
import { createSocket } from "../../services/socket";

import KpiTile from "../results/KpiTile";
import FiltersPanel from "../results/FiltersPanel";
import ActivityFeed from "../results/ActivityFeed";

import {
  getCrawlResults,
  getCrawlTasks,
  getAllCrawlResults,
  deleteDocument,
} from "../../services/web-services";
import { getAllResultsCount } from "../../services/generic_services";
import { getSubreddits, getRedditTasks } from "../../services/reddit_services";
import { getMastodonResults, getMastodonTasks } from "../../services/mastodon_services";
// Telegram UI hidden — kept for future re-enable.
// import { getTelegramResults, getTelegramTasks } from "../../services/telegram_services";

const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const toCsv = (rows, columns) => {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const body = rows
    .map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
    .join("\n");
  return `﻿${header}\n${body}\n`;
};
const downloadBlob = (content, mime, filename) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const baseURL = getEnv("REACT_APP_API_URL");

const SOURCE_META = {
  web: { label: "Web Documents", color: "#4FB6BC", icon: <PublicIcon fontSize="small" /> },
  reddit: { label: "Reddit Posts", color: "#FF4500", icon: <RedditIcon fontSize="small" /> },
  mastodon: { label: "Mastodon Posts", color: "#6364FF", icon: <ForumIcon fontSize="small" /> },
  // Telegram UI hidden — kept for future re-enable.
  // telegram: { label: "Telegram Messages", color: "#229ED9", icon: <TelegramIcon fontSize="small" /> },
};

const inDateRange = (dateLike, range) => {
  if (range === "all" || !dateLike) return true;
  const d = new Date(dateLike).getTime();
  if (Number.isNaN(d)) return true;
  const now = Date.now();
  const cutoffMs = { "24h": 86400e3, "7d": 7 * 86400e3, "30d": 30 * 86400e3 }[range];
  return cutoffMs ? now - d <= cutoffMs : true;
};

const SectionHeader = ({ title, action }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="h6">{title}</Typography>
    {action}
  </Box>
);

const ResultsPage = () => {
  const theme = useTheme();
  const { modules } = useModules();
  const socketRef = useRef(null);
  const [searchParams] = useSearchParams();
  const site_id = searchParams.get("site_id");

  const [resultsCount, setResultsCount] = useState({});
  const [recentItems, setRecentItems] = useState([]);
  const [taskCounts, setTaskCounts] = useState({ web: 0, reddit: 0, mastodon: 0 });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    dateRange: "all",
    confidenceMin: 0,
    sources: { web: true, reddit: true, mastodon: true },
  });

  const toggleSource = (key) =>
    setFilters((f) => ({ ...f, sources: { ...f.sources, [key]: !f.sources[key] } }));

  const resetFilters = () =>
    setFilters({
      search: "",
      dateRange: "all",
      confidenceMin: 0,
      sources: { web: true, reddit: true, mastodon: true },
    });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        counts,
        webRes,
        webTasks,
        subreddits,
        redditTasks,
        mastoRes,
        mastoTasks,
        // Telegram UI hidden — positions kept so existing destructuring lines up.
        _tgRes,
        _tgTasks,
      ] = await Promise.allSettled([
        getAllResultsCount(),
        modules.crawlers ? getCrawlResults({ page: 1, site_id }) : Promise.resolve([]),
        modules.crawlers ? getCrawlTasks() : Promise.resolve([]),
        modules.social_crawlers ? getSubreddits({ page: 1 }) : Promise.resolve([]),
        modules.social_crawlers ? getRedditTasks() : Promise.resolve([]),
        modules.social_crawlers ? getMastodonResults({ page: 1 }) : Promise.resolve([]),
        modules.social_crawlers ? getMastodonTasks() : Promise.resolve([]),
        // Telegram UI hidden — kept for future re-enable.
        // modules.social_crawlers ? getTelegramResults({ page: 1 }) : Promise.resolve([]),
        // modules.social_crawlers ? getTelegramTasks() : Promise.resolve([]),
        Promise.resolve([]),
        Promise.resolve([]),
      ]);

      if (counts.status === "fulfilled") setResultsCount(counts.value || {});
      setTaskCounts({
        web: webTasks.status === "fulfilled" ? (webTasks.value || []).length : 0,
        reddit: redditTasks.status === "fulfilled" ? (redditTasks.value || []).length : 0,
        mastodon: mastoTasks.status === "fulfilled" ? (mastoTasks.value || []).length : 0,
        // Telegram UI hidden — kept for future re-enable.
        // telegram: tgTasks.status === "fulfilled" ? (tgTasks.value || []).length : 0,
      });

      // Normalize each source's recent items into a unified shape:
      // { source, sourceLabel, title, subtitle, created_at, confidence, url, id }
      const items = [];

      if (webRes.status === "fulfilled" && Array.isArray(webRes.value)) {
        webRes.value.forEach((d) => {
          items.push({
            source: "web",
            sourceLabel: d.site_name || d.url || "Web",
            title: d.title || d.url || "Untitled document",
            subtitle: d.url,
            created_at: d.created_at || d.date,
            confidence: typeof d.confidence === "number" ? d.confidence : undefined,
            url: d.url,
            id: d._id || d.id || d.url,
          });
        });
      }

      if (subreddits.status === "fulfilled" && Array.isArray(subreddits.value)) {
        subreddits.value.forEach((sub) => {
          (sub.submissions || []).slice(0, 5).forEach((s) => {
            items.push({
              source: "reddit",
              sourceLabel: `r/${sub.subreddit}`,
              title: s.submission || s.title || "(no title)",
              subtitle: s.author ? `u/${s.author}` : null,
              created_at: s.created_utc ? new Date(s.created_utc * 1000).toISOString() : null,
              confidence: typeof s.confidence === "number" ? s.confidence : undefined,
              url: s.url,
              id: s.submission_id || s.name,
            });
          });
        });
      }

      if (mastoRes.status === "fulfilled" && Array.isArray(mastoRes.value)) {
        mastoRes.value.forEach((entry) => {
          const p = entry.post || {};
          items.push({
            source: "mastodon",
            sourceLabel: `#${entry.hashtag} @ ${entry.instance}`,
            title: (p.text || "").slice(0, 140) || "(empty post)",
            subtitle: p.author ? `@${p.author}` : null,
            created_at: p.created_at,
            confidence: typeof p.confidence === "number" ? p.confidence : undefined,
            url: p.url,
            id: p.post_id,
          });
        });
      }

      // Telegram UI hidden — kept for future re-enable.
      // if (tgRes.status === "fulfilled" && Array.isArray(tgRes.value)) {
      //   tgRes.value.forEach((entry) => {
      //     const m = entry.message || {};
      //     items.push({
      //       source: "telegram",
      //       sourceLabel: entry.channel,
      //       title: (m.text || "").slice(0, 140) || "(empty message)",
      //       subtitle: typeof m.views === "number" ? `${m.views} views` : null,
      //       created_at: m.created_at,
      //       confidence: typeof m.confidence === "number" ? m.confidence : undefined,
      //       id: m.message_id,
      //     });
      //   });
      // }

      // Sort by date desc, take latest 50
      items.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setRecentItems(items.slice(0, 50));
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [modules.crawlers, modules.social_crawlers, site_id]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Live updates: re-fetch on new socket events (debounced)
  useEffect(() => {
    if (!baseURL || !(modules.crawlers || modules.social_crawlers)) return;
    const socket = createSocket();
    socketRef.current = socket;
    const debounced = _.debounce(fetchAll, 1500);
    // Telegram UI hidden — "telegram_message" event removed; kept for future re-enable.
    ["new_document", "new_posts", "new_tweet", "mastodon_post"].forEach((evt) =>
      socket.on(evt, debounced)
    );
    return () => {
      debounced.cancel();
      socket.disconnect();
    };
  }, [fetchAll, modules.crawlers, modules.social_crawlers]);

  // Filtered view (sources / dateRange / confidence / search)
  const filteredItems = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return recentItems.filter((it) => {
      if (!filters.sources[it.source]) return false;
      if (!inDateRange(it.created_at, filters.dateRange)) return false;
      if ((it.confidence ?? 0) < filters.confidenceMin) return false;
      if (q) {
        const hay = [it.title, it.sourceLabel, it.subtitle, it.url].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [recentItems, filters]);

  // Counts per source (for KPI + distribution donut)
  const sourceCounts = useMemo(
    () => ({
      web: resultsCount.documents ?? 0,
      reddit: resultsCount.submissions ?? 0,
      mastodon: resultsCount.mastodon ?? 0,
      // Telegram UI hidden — kept for future re-enable.
      // telegram: resultsCount.telegram ?? 0,
    }),
    [resultsCount]
  );

  const distributionData = useMemo(
    () =>
      Object.entries(sourceCounts)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => ({ name: SOURCE_META[k].label, value: v, color: SOURCE_META[k].color })),
    [sourceCounts]
  );

  const volumeData = useMemo(
    () =>
      Object.entries(sourceCounts).map(([k, v]) => ({
        source: SOURCE_META[k].label.replace(" Documents", "").replace(" Posts", "").replace(" Messages", ""),
        count: v,
        color: SOURCE_META[k].color,
      })),
    [sourceCounts]
  );

  const sourceColorsMap = useMemo(
    () => Object.fromEntries(Object.entries(SOURCE_META).map(([k, v]) => [k, v.color])),
    []
  );

  const sourcesForFilter = useMemo(
    () =>
      Object.entries(SOURCE_META).map(([k, v]) => ({
        key: k,
        label: v.label,
        color: v.color,
        checked: filters.sources[k],
        count: sourceCounts[k],
      })),
    [filters.sources, sourceCounts]
  );

  const [exportAnchor, setExportAnchor] = useState(null);

  const exportRecent = useCallback((format) => {
    const date = new Date().toISOString().slice(0, 10);
    const base = `cti-recent-${date}`;
    if (format === "json") {
      downloadBlob(JSON.stringify(filteredItems, null, 2), "application/json", `${base}.json`);
    } else {
      const cols = [
        { label: "source", get: (r) => r.source },
        { label: "source_label", get: (r) => r.sourceLabel },
        { label: "title", get: (r) => r.title },
        { label: "subtitle", get: (r) => r.subtitle },
        { label: "created_at", get: (r) => r.created_at },
        { label: "url", get: (r) => r.url },
        { label: "confidence", get: (r) => r.confidence },
      ];
      downloadBlob(toCsv(filteredItems, cols), "text/csv;charset=utf-8", `${base}.csv`);
    }
    setExportAnchor(null);
  }, [filteredItems]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Threat Information Gathering
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unified view across web, Reddit and Mastodon sources
            {lastUpdated && (
              <Box component="span" sx={{ color: "text.disabled", ml: 1 }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </Box>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            onClick={(e) => setExportAnchor(e.currentTarget)}
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon fontSize="small" />}
            endIcon={<ArrowDropDownIcon fontSize="small" />}
            disabled={filteredItems.length === 0}
          >
            Export
          </Button>
          <Menu
            anchorEl={exportAnchor}
            open={Boolean(exportAnchor)}
            onClose={() => setExportAnchor(null)}
          >
            <MenuItem onClick={() => exportRecent("json")}>
              <ListItemIcon><DataObjectIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="JSON" secondary="Full structure" />
            </MenuItem>
            <MenuItem onClick={() => exportRecent("csv")}>
              <ListItemIcon><TableViewIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="CSV" secondary="Flat, Excel-ready" />
            </MenuItem>
          </Menu>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {modules.crawlers && (
          <Grid item xs={12} sm={6} md={4}>
            <KpiTile
              label="Web"
              value={sourceCounts.web}
              unit="documents"
              accent={SOURCE_META.web.color}
              icon={SOURCE_META.web.icon}
              navigateTo="/tasks"
              loading={loading}
              caption={`${taskCounts.web} active source${taskCounts.web === 1 ? "" : "s"}`}
            />
          </Grid>
        )}
        {modules.social_crawlers && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <KpiTile
                label="Reddit"
                value={sourceCounts.reddit}
                unit="posts"
                accent={SOURCE_META.reddit.color}
                icon={SOURCE_META.reddit.icon}
                navigateTo="/reddit/tasks"
                loading={loading}
                caption={`${taskCounts.reddit} subreddit${taskCounts.reddit === 1 ? "" : "s"}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <KpiTile
                label="Mastodon"
                value={sourceCounts.mastodon}
                unit="posts"
                accent={SOURCE_META.mastodon.color}
                icon={SOURCE_META.mastodon.icon}
                navigateTo="/mastodon/tasks"
                loading={loading}
                caption={`${taskCounts.mastodon} hashtag${taskCounts.mastodon === 1 ? "" : "s"}`}
              />
            </Grid>
            {/* Telegram KPI tile hidden — kept for future re-enable.
            <Grid item xs={12} sm={6} md={3}>
              <KpiTile
                label="Telegram"
                value={sourceCounts.telegram}
                unit="messages"
                accent={SOURCE_META.telegram.color}
                icon={SOURCE_META.telegram.icon}
                navigateTo="/telegram/tasks"
                loading={loading}
                caption={`${taskCounts.telegram} channel${taskCounts.telegram === 1 ? "" : "s"}`}
              />
            </Grid>
            */}
          </>
        )}
        {/* Telegram SourceCard hidden — kept for future re-enable.
        {modules.social_crawlers && (
          <SourceCard
            title={"Telegram Results"}
            sourcesNumber={numberOfTelegramTasks}
            documentsNumber={numberOfTelegramMessages}
            sourcesIcon={
              <TelegramIcon sx={{ fontSize: "80px", color: "primary.main" }} />
            }
            sourcesTitle={"Channels"}
            documentsTitle={"Messages"}
            documentsIcon={
              <ArticleIcon sx={{ fontSize: "80px", color: "primary.main" }} />
            }
            navigation_url="/telegram/tasks"
            results_url="/telegram/results"
            handleVisibility={handleTelegramVisibility}
          />
        )}
        */}
        {/* Twitter SourceCard hidden — kept for future re-enable.
        {modules.social_crawlers && (
          <SourceCard
            title={"Social Media Results"}
            sourcesNumber={numberOfTwitterTasks}
            documentsNumber={numberOfTweets}
            sourcesIcon={
              <XIcon sx={{ fontSize: "80px", color: "primary.main" }} />
            }
            sourcesTitle={"Social Media Sources"}
            documentsTitle={"Posts"}
            documentsIcon={
              <ArticleIcon sx={{ fontSize: "80px", color: "primary.main" }} />
            }
            navigation_url={"/social_media/tasks"}
            handleVisibility={handleTwitterVisibility}
          />
        )}
        */}
      </Grid>

      {/* Filters + content grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <FiltersPanel
            sources={sourcesForFilter}
            onSourceToggle={toggleSource}
            search={filters.search}
            onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
            dateRange={filters.dateRange}
            onDateRangeChange={(v) => setFilters((f) => ({ ...f, dateRange: v }))}
            confidenceMin={filters.confidenceMin}
            onConfidenceChange={(v) => setFilters((f) => ({ ...f, confidenceMin: v }))}
            onReset={resetFilters}
          />
        </Grid>

        <Grid item xs={12} md={9}>
          {/* Charts row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={7}>
              <Card sx={{ p: 2, height: 280 }}>
                <SectionHeader title="Volume by source" />
                <Box sx={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <BarChart data={volumeData} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                      <XAxis dataKey="source" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                      <YAxis tick={{ fontSize: 11, fill: theme.palette.text.secondary }} allowDecimals={false} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                          fontSize: 12,
                        }}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {volumeData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={5}>
              <Card sx={{ p: 2, height: 280 }}>
                <SectionHeader title="Source distribution" />
                <Box sx={{ width: "100%", height: 220 }}>
                  {distributionData.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.disabled">
                        No data yet
                      </Typography>
                    </Box>
                  ) : (
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={distributionData}
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                          stroke="none"
                        >
                          {distributionData.map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 8,
                            fontSize: 12,
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconType="circle"
                          wrapperStyle={{ fontSize: 11 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Activity feed */}
          <ActivityFeed
            items={filteredItems}
            sourceColors={sourceColorsMap}
            emptyMessage={
              recentItems.length === 0
                ? "No results yet. Start a crawl from one of the source pages."
                : "No items match the current filters."
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResultsPage;
